import { useEffect } from 'react'
import { SITE } from '../data/site'

interface SeoProps {
  title: string
  description?: string
  path?: string
  videoId?: string
  /** When true, use `title` as the full document title (no site-name suffix). */
  skipTitleSuffix?: boolean
  /** Optional JSON-LD graph/object injected as application/ld+json. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  /** e.g. "noindex, nofollow" for trial pages. */
  robots?: string
}

export function useSeo({ title, description, path = '', videoId, skipTitleSuffix, jsonLd, robots }: SeoProps) {
  useEffect(() => {
    const fullTitle = skipTitleSuffix ? title : `${title} | ${SITE.nameTa}`
    document.title = fullTitle

    const desc = description ?? SITE.taglineTa
    let meta = document.querySelector('meta[name="description"]')
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', 'description')
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', desc)

    let robotsMeta = document.querySelector('meta[name="robots"]')
    if (robots) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta')
        robotsMeta.setAttribute('name', 'robots')
        document.head.appendChild(robotsMeta)
      }
      robotsMeta.setAttribute('content', robots)
    } else if (robotsMeta) {
      robotsMeta.setAttribute('content', 'index, follow')
    }

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = `https://${SITE.domain}${path}`

    function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
      let el = document.querySelector(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }

    const image = videoId
      ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
      : `https://${SITE.domain}${SITE.ogImage}`
    upsertMeta('property', 'og:title', fullTitle)
    upsertMeta('property', 'og:description', desc)
    upsertMeta('property', 'og:image', image)
    upsertMeta('property', 'og:url', `https://${SITE.domain}${path}`)
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:image', image)

    const scriptId = 'do-jsonld'
    document.getElementById(scriptId)?.remove()

    let payload: unknown = null
    if (jsonLd) {
      payload = jsonLd
    } else if (path.startsWith('/content/') && videoId) {
      payload = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Article',
            headline: title,
            description: desc,
            inLanguage: 'ta',
            mainEntityOfPage: `https://${SITE.domain}${path}`,
            publisher: { '@type': 'Organization', name: SITE.nameEn, url: `https://${SITE.domain}` },
          },
          {
            '@type': 'VideoObject',
            name: title,
            description: desc,
            thumbnailUrl: image,
            embedUrl: `https://www.youtube.com/embed/${videoId}`,
            contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
          },
        ],
      }
    }

    if (payload) {
      const script = document.createElement('script')
      script.id = scriptId
      script.type = 'application/ld+json'
      script.text = JSON.stringify(payload)
      document.head.appendChild(script)
    }

    return () => {
      document.getElementById(scriptId)?.remove()
    }
  }, [title, description, path, videoId, skipTitleSuffix, jsonLd, robots])
}
